import { useCallback, useEffect, useState } from "react";
import { client } from "../../lib/apollo";
import useNav from "../useNavigation";
import { toast } from "react-hot-toast";
import { SignInFormData } from "../../pages/signin";
import { SignUpFormData } from "../../pages/signup";

interface UseAuthReturn {
  isAuthenticated: boolean;
  userId: string | null;
  logout: () => void;
  verifyToken: () => Promise<void>;
  isAuthLoading: boolean;
  signIn: (formData: SignInFormData) => Promise<void>;
  signUp: (formData: SignUpFormData) => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const navigateWithLog = useNav();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setIsAuthenticated(false);
      setUserId(null);
      return;
    }

    try {
      const response = await fetch(
        import.meta.env.VITE_API_URL + "/auth/verify",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Invalid token");
      }

      const data = await response.json();
      setUserId(data.userId);
      setIsAuthenticated(true);
    } catch {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    localStorage.clear();

    setIsAuthenticated(false);
    setUserId(null);

    await client.resetStore();

    navigateWithLog("/");
  }, [navigateWithLog]);

  const signIn = async (formData: SignInFormData) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        import.meta.env.VITE_API_URL + "/auth/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error(
            "Trop de tentatives de connexion. Veuillez réessayer plus tard."
          );
        }
        throw new Error("Ces identifiants ne sont pas corrects");
      }

      const data = await response.json();

      localStorage.setItem("token", data.access_token);

      window.location.reload();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Une erreur est survenue";
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (formData: SignUpFormData) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        import.meta.env.VITE_API_URL + "/auth/register",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error("Trop de tentatives. Veuillez réessayer plus tard.");
        }
        const errorData = await response.json();
        throw new Error(errorData.message);
      }

      const data = await response.json();

      localStorage.setItem("token", data.access_token);

      toast.success("Compte créé avec succès");

      window.location.reload();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Une erreur est survenue";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    verifyToken().finally(() => {
      setIsLoading(false);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isAuthenticated,
    userId,
    logout,
    verifyToken,
    isAuthLoading: isLoading,
    signIn,
    signUp,
  };
}
